import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface Projektaufgabe {
  id: string;
  beschreibung: string;
  wiederkehrend: number;
  periodizitaet: number;             // from environment JSON array
  kunde_id: string;
  techniker_id?: string;             // replaces verantwortlicher
  serie_start?: string;              // date string
  serie_ende?: string;               // date string
  anmerkungen?: string;              // longtext
  soll_zeit?: number;                // number
  monat_plan?: string;               // string
  wochentag_plan?: string;           // string
  anmeldung_angefordert?: string;    // string
  standort_id?: string;
  einsatztyp: string;                // from environment JSON array
  is_active?: boolean;               // single source of truth for status
  activated_at?: string;             // activation timestamp
  created_at?: string;
  updated_at?: string;
  created_by?: string;               // foreign key
  updated_by?: string;               // foreign key
  
  // Relationships (loaded via with())
  kunde?: Kunde;
  techniker?: Mitarbeiter;           // replaces verantwortlicherMitarbeiter
  standort?: Standort;
  createdBy?: Mitarbeiter;           // replaces verantwortlicherMitarbeiter
  updatedBy?: Mitarbeiter;           // new relationship
  kontrolltermine?: Kontrolltermin[];
}

export interface Kunde {
  id: string;
  suchbegriff: string;
  name: string;
}

export interface Standort {
  id: string;
  name: string;
  nummer: number;
}

export interface ProjektaufgabePeriodizitaet {
  id: number;
  code: string;
  beschreibung: string;
}

export interface ProjektaufgabeStatus {
  id: number;
  code: string;
  beschreibung: string;
}

export interface Mitarbeiter {
  id: string; // UUID from database
  name: string;
  vorname: string;
  nachname: string;
}

export interface Kontrolltermin {
  id: string;
  einsatznummer: number;
  standort_id: string;
  projektaufgabe_id: string;
  kunde_id: string;
  techniker_id?: string;
  objektstruktur_indikatoren?: any; // JSON array
  anmerkungen?: string;
  soll_zeit?: number;
  ist_zeit?: number;
  monat_plan?: string;
  wochentag_plan?: string;
  einsatztyp: string;
  datum_durchfuerung?: string;
  status_id: number;
  global_befall_status_id?: number;
  global_befall?: number;
  zusatz_massnahme?: string;
  freigabe_id?: string;
  created_at?: string;
  updated_at?: string;
  
  // Relationships
  projektaufgabe?: any; // Projektaufgabe interface
  standort?: Standort;
  kunde?: Kunde;
  techniker?: Mitarbeiter;
  freigabe?: any; // Freigabe interface
  kontrollIndikatoren?: any[]; // KontrollIndikator interface array
}

export interface KontrollterminStatus {
  id: number;
  code: string;
  beschreibung: string;
}

export interface Kontakt {
  id: string;
  name: string;
  email: string;
  telefon: string;
}

export interface Massnahme {
  id: string;
  kontrolltermin_id: string;
  beschreibung: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ProjektaufgabeDropdowns {
  periodizitaeten: ProjektaufgabePeriodizitaet[];
  status_options: ProjektaufgabeStatus[];
  kunden: Kunde[];
  mitarbeiter: Mitarbeiter[];
  standorte: Standort[];
}

export interface ProjektaufgabePagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface ProjektaufgabeResponse {
  data: Projektaufgabe[];
  pagination: ProjektaufgabePagination;
}

@Injectable({
  providedIn: 'root'
})
export class ProjektaufgabeService {
  private apiUrl = `${environment.apiUrl}/projektaufgabe`;

  constructor(private http: HttpClient) {}

  /**
   * Get all projektaufgaben with pagination and filters
   */
  getProjektaufgaben(
    page: number = 1,
    limit: number = 10,
    search?: string,
    kunde_id?: string,
    standort_id?: string,
    verantwortlicher_id?: string,
    workflowstatus_id?: string
  ): Observable<ProjektaufgabeResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }
    if (kunde_id) {
      params = params.set('kunde_id', kunde_id);
    }
    if (standort_id) {
      params = params.set('standort_id', standort_id);
    }
    if (verantwortlicher_id) {
      params = params.set('verantwortlicher_id', verantwortlicher_id);
    }
    if (workflowstatus_id) {
      params = params.set('workflowstatus_id', workflowstatus_id);
    }

    return this.http.get<ProjektaufgabeResponse>(this.apiUrl, { params });
  }

  /**
   * Get single projektaufgabe by ID
   */
  getProjektaufgabe(id: string): Observable<Projektaufgabe> {
    return this.http.get<Projektaufgabe>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get the active projektaufgabe for a given standort
   */
  getActiveProjektaufgabe(standortId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/active/${standortId}`);
  }

  /**
   * Create new projektaufgabe
   */
  createProjektaufgabe(projektaufgabe: Partial<Projektaufgabe>): Observable<Projektaufgabe> {
    return this.http.post<Projektaufgabe>(this.apiUrl, projektaufgabe);
  }

  /**
   * Update existing projektaufgabe
   */
  updateProjektaufgabe(id: string, projektaufgabe: Partial<Projektaufgabe>): Observable<Projektaufgabe> {
    return this.http.put<Projektaufgabe>(`${this.apiUrl}/${id}`, projektaufgabe);
  }

  /**
   * Delete projektaufgabe
   */
  deleteProjektaufgabe(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${id}`);
  }

  /**
   * Get dropdown data for forms
   */
  getProjektaufgabeDropdowns(): Observable<ProjektaufgabeDropdowns> {
    return this.http.get<ProjektaufgabeDropdowns>(`${this.apiUrl}-dropdowns`);
  }

  /**
   * Activate a projektaufgabe
   */
  activateProjektaufgabe(projektaufgabeId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/${projektaufgabeId}/activate`, {});
  }

  /**
   * Deactivate a projektaufgabe
   */
  deactivateProjektaufgabe(projektaufgabeId: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/${projektaufgabeId}/deactivate`, {});
  }

}
